import { ThemeEvents } from '@theme/events';
import { formatMoney as formatMoneyValue } from '@theme/money-formatting';

/**
 * Handles quantity-based total price updates for any price element.
 * Works with both product-price and subscription-price elements.
 * Elements must have data-show-total-price="true" to opt-in.
 */

/**
 * Formats a money value using the shop's money format.
 * The format template ({{ shop.money_format }} / money_with_currency) and
 * the active cart currency are server-rendered onto the price element by
 * blocks/price.liquid and blocks/subscription-price.liquid; the formatting
 * itself delegates to the theme's standard money-formatting module (the
 * client-side replica of the Liquid money filter, new in Horizon 4.1.1).
 * @param {number} cents - The value in cents/subunits.
 * @param {HTMLElement} element - The element containing money format templates.
 * @returns {string} The formatted money string.
 */
function formatMoney(cents, element) {
  const moneyFormatWithCurrencyTemplate = element.querySelector('template[ref="moneyFormatWithCurrency"]');
  const moneyFormatTemplate = element.querySelector('template[ref="moneyFormat"]');

  const template =
    (moneyFormatWithCurrencyTemplate instanceof HTMLTemplateElement
      ? moneyFormatWithCurrencyTemplate.content.textContent
      : null) ||
    (moneyFormatTemplate instanceof HTMLTemplateElement
      ? moneyFormatTemplate.content.textContent
      : null) ||
    '{{amount}}';

  const currency = element.dataset.currency || 'USD';

  return formatMoneyValue(cents, template, currency);
}

/**
 * Updates the price display for an element based on quantity.
 * @param {HTMLElement} element - The price element to update.
 * @param {number} quantity - The quantity multiplier.
 */
function updatePriceElement(element, quantity) {
  const basePrice = parseInt(element.dataset.basePrice, 10);
  if (isNaN(basePrice)) return;

  const totalPrice = basePrice * quantity;
  const formattedPrice = formatMoney(totalPrice, element);

  // Determine selectors based on element type
  const isSubscription = element.tagName.toLowerCase() === 'subscription-price';
  const priceSelector = isSubscription ? '.subscription-price__current' : '.price:not(.compare-at-price)';
  const compareSelector = isSubscription ? '.subscription-price__compare' : '.compare-at-price';
  const compareAttr = isSubscription ? 'baseComparePrice' : 'baseCompareAtPrice';

  // Update main price
  const priceEl = element.querySelector(priceSelector);
  if (priceEl) {
    priceEl.textContent = formattedPrice;
  }

  // Update compare-at price if exists
  const baseComparePrice = parseInt(element.dataset[compareAttr], 10);
  if (!isNaN(baseComparePrice)) {
    const totalComparePrice = baseComparePrice * quantity;
    const formattedComparePrice = formatMoney(totalComparePrice, element);
    const compareEl = element.querySelector(compareSelector);
    if (compareEl) {
      compareEl.textContent = formattedComparePrice;
    }
  }
}

/**
 * Handles quantity selector update events.
 * @param {CustomEvent} event - The quantity selector update event.
 */
function handleQuantityChange(event) {
  const quantity = event.detail?.quantity;
  if (typeof quantity !== 'number' || quantity < 1) return;

  // Find the section containing this event
  const section = event.target?.closest('.shopify-section, dialog');
  if (!section) return;

  // Find all price elements with show_total_price enabled
  const priceElements = section.querySelectorAll('[data-show-total-price="true"]');
  priceElements.forEach((element) => {
    if (element instanceof HTMLElement) {
      updatePriceElement(element, quantity);
    }
  });
}

// Listen for quantity changes at document level
document.addEventListener(ThemeEvents.quantitySelectorUpdate, handleQuantityChange);
